'use client';

import SvgTextfieldclose from '@common/__generated__/icon/components/SvgTextfieldclose';
import { ko } from 'date-fns/locale';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import type { DateRange as PickerDateRange } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';

import { Input } from '../Input/Input';

export type DateRange = PickerDateRange;

interface DateRangePickerProps {
  dateRange?: DateRange;
  onChange: (dateRange?: DateRange) => void;
  placeholder?: string;
  dimensions?: 'H40';
}

const TEXT_VALUE_MAX_LENGTH = 29;

export const DateRangePicker = ({
  dateRange,
  onChange,
  placeholder = '날짜를 선택하세요',
  dimensions,
}: DateRangePickerProps) => {
  const pickerRef = useRef<HTMLDivElement>(null);

  const [dateRangeText, setDateRangeText] = useState('');

  const [isOpen, setIsOpen] = useState(false);

  //* 외부 클릭시 이벤트
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  //* dateRangeText 동기화
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      setDateRangeText(
        `${dayjs(dateRange.from).format('YYYY. MM. DD.')} ~ ${dayjs(dateRange.to).format('YYYY. MM. DD.')}`,
      );
    } else if (dateRange?.from) {
      setDateRangeText(dayjs(dateRange.from).format('YYYY. MM. DD. ~ '));
    } else {
      setDateRangeText('');
    }
  }, [dateRange?.from, dateRange?.to]);

  const handleDayClick = (day: Date) => {
    if (!dateRange?.from || (dateRange.from && dateRange.to)) {
      onChange({ from: day, to: undefined });
    } else if (dateRange.from && !dateRange.to && day > dateRange.from) {
      onChange({ ...dateRange, to: day });
    }
  };

  const formatDateRange = (value: string): string => {
    //* 입력된 값이 숫자일 경우와 길이가 8인 경우 날짜 형식으로 변환
    //* 예를 들어, 20230415 형태의 문자열을 YYYY. MM. DD로 변환
    if (/^\d+$/.test(value) && value.length === 8) {
      const year = value.slice(0, 4);
      const month = value.slice(4, 6);
      const day = value.slice(6, 8);

      return `${year}. ${month}. ${day}. ~ `;
    }

    //* 문자열이 15글자를 넘어간 경우
    if (value.length > 15) {
      const potentialDate = value.slice(-8); //* 문자열의 마지막 8글자를 추출하여 잠재적인 날짜로 간주
      //* 마지막 8글자가 모두 숫자로 되어 있는지 확인
      if (/^\d+$/.test(potentialDate) && potentialDate.length === 8) {
        const year = potentialDate.slice(0, 4); //* 추출된 8글자 중 처음 4글자를 연도로 사용
        const month = potentialDate.slice(4, 6); //* 다음 2글자를 월로 사용
        const day = potentialDate.slice(6, 8); //* 마지막 2글자를 일로 사용

        //* 기존 문자열에서 마지막 8글자를 제외한 부분과 변환된 날짜 형식을 결합하여 반환
        return `${value.slice(0, -8)}${year}. ${month}. ${day}.`;
      }
    }

    return value;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedTextValue = formatDateRange(event.target.value.trim());
    setDateRangeText(formattedTextValue);

    const [fromText, toText] = formattedTextValue.split('~');

    //* text input이 유효한 경우에만 값 업데이트
    if (
      //* 유효한 textValue 길이
      formattedTextValue.length === TEXT_VALUE_MAX_LENGTH &&
      dayjs(fromText).isValid() &&
      dayjs(toText).isValid()
    ) {
      onChange({
        from: new Date(fromText),
        to: new Date(toText),
      });
    }
  };

  return (
    <div ref={pickerRef} className="relative">
      <Input
        dimensions={dimensions}
        autoComplete="off"
        className="min-w-290 rounded-xsmall"
        maxLength={TEXT_VALUE_MAX_LENGTH}
        placeholder={placeholder}
        rightContent={
          dateRangeText ? (
            <div className="flex items-center">
              <button
                className="flex size-30 items-center justify-center"
                onClick={(event) => {
                  setDateRangeText('');
                  onChange();
                  event.stopPropagation();
                }}
              >
                <SvgTextfieldclose className="size-18" />
              </button>
            </div>
          ) : undefined
        }
        value={dateRangeText}
        onChange={handleInputChange}
        onFocus={() => {
          setIsOpen(true);
        }}
      />
      {isOpen ? (
        <div className="absolute top-full z-10 w-300 max-w-300 rounded-xsmall border-1 border-Gray-300 bg-Gray-white p-10">
          <DayPicker
            classNames={{
              caption: 'flex justify-between items-center',
              nav: 'flex h-40',
              nav_button: 'w-40 h-40 flex justify-center items-center',
              button: 'w-full h-full',
              cell: 'w-40 h-40 text-center',
              day_selected: 'bg-Gray-black text-Gray-white',
              day_range_start: '!bg-Gray-900 rounded-s-pill',
              day_range_end: '!bg-Gray-900 rounded-e-pill',
              day_range_middle: '!bg-Gray-500',
            }}
            locale={ko}
            mode="range"
            selected={dateRange}
            onDayClick={handleDayClick}
          />
        </div>
      ) : null}
    </div>
  );
};
